import { redirect } from 'next/navigation';

/**
 * The platform answers 401 `{"error":"reauthentication_required"}` from any
 * organization or environment route when a security-critical identity change
 * (a sign-in provider swap, for example) has invalidated the session. This is
 * the one place the dashboard turns that into "go and sign in again": every
 * server-side SDK client is built through `withReauthRedirect`, so no page or
 * action handles it itself.
 */
export const REAUTH_ERROR = 'reauthentication_required';

export const REAUTH_MESSAGE =
  'Your sign-in settings changed. Please sign in again.';

/** Where a session that must be renewed is sent. The login page clears the stale sign-in first. */
export const REAUTH_LOGIN_PATH = `/login?reason=${REAUTH_ERROR}`;

/** True when a decoded error body is the platform's re-authentication answer. */
export function isReauthenticationRequired(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const b = body as { error?: unknown; code?: unknown };
  return b.error === REAUTH_ERROR || b.code === REAUTH_ERROR;
}

interface InterceptingClient {
  interceptors: {
    response: {
      use: (fn: (response: Response, ...rest: any[]) => Response | Promise<Response>) => unknown;
    };
  };
}

/**
 * Registers the re-authentication check on a freshly built SDK client and
 * returns the same client. Throws Next's redirect, which server components,
 * server actions and route handlers all honour.
 */
export function withReauthRedirect<C extends InterceptingClient>(client: C): C {
  client.interceptors.response.use(async (response) => {
    if (response.status === 401) {
      const body = await response
        .clone()
        .json()
        .catch(() => null);
      if (isReauthenticationRequired(body)) redirect(REAUTH_LOGIN_PATH);
    }
    return response;
  });
  return client;
}

/** True when a thrown value is the redirect `withReauthRedirect` raises. */
export function isReauthRedirect(error: unknown): boolean {
  const digest = (error as { digest?: unknown } | null)?.digest;
  return (
    typeof digest === 'string' &&
    digest.startsWith('NEXT_REDIRECT') &&
    digest.includes(REAUTH_LOGIN_PATH)
  );
}

/**
 * For JSON route handlers: a catch block calls this first. The interceptor's
 * redirect would otherwise be swallowed (or, uncaught, become a 307 to an HTML
 * page the caller cannot use), so a route answers with the same 401 code the
 * platform used and the browser-side guard (`ReauthGuard`) navigates to sign-in.
 * Returns null for any other error.
 */
export function reauthRouteResponse(error: unknown): Response | null {
  if (!isReauthRedirect(error)) return null;
  return Response.json(
    { error: REAUTH_ERROR, message: REAUTH_MESSAGE },
    { status: 401, headers: { 'Cache-Control': 'no-store' } },
  );
}

/**
 * Wraps a JSON route handler so the interceptor's redirect, once a catch block
 * has let it through (`unstable_rethrow`), becomes the 401 `reauthentication_required`
 * answer instead of a 307 to an HTML page.
 */
export function withReauthJson<A extends unknown[], R>(
  handler: (...args: A) => Promise<R> | R,
): (...args: A) => Promise<R | Response> {
  return async (...args: A) => {
    try {
      return await handler(...args);
    } catch (error) {
      const response = reauthRouteResponse(error);
      if (response) return response;
      throw error;
    }
  };
}
